import React, { useState, useEffect } from 'react';
import { ChoiceCards, EtlBanner, EtlLoading } from '../EtlPages';
import * as XLSX from 'xlsx';

const ExcelSheetSelector = ({ file, onSheetSelection, initialSelectedSheet }) => {
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(initialSelectedSheet || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sheetPreview, setSheetPreview] = useState({});

  useEffect(() => {
    if (file) {
      loadSheetInfo();
    }
  }, [file]);

  const loadSheetInfo = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();

      // Check if file is CSV
      const isCsv = file.name.toLowerCase().endsWith('.csv');

      const workbook = XLSX.read(arrayBuffer, {
        type: 'array',
        // For CSV files, treat as single sheet
        ...(isCsv && { raw: true })
      });

      // Get sheet names and basic info
      const sheetData = workbook.SheetNames.map((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const rowCount = range.e.r - range.s.r + 1;
        const colCount = range.e.c - range.s.c + 1;

        // Get preview data (first 5 rows)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          range: { s: { r: 0, c: 0 }, e: { r: Math.min(4, range.e.r), c: range.e.c } }
        });

        return {
          index: index,
          name: isCsv ? file.name : sheetName,
          rowCount: rowCount,
          columnCount: colCount,
          preview: jsonData
        };
      });

      setSheets(sheetData);

      // Create preview data for display
      const previews = {};
      sheetData.forEach(sheet => {
        previews[sheet.name] = sheet.preview;
      });
      setSheetPreview(previews);

      // If no initial selection, default to first sheet or "all"
      if (!initialSelectedSheet) {
        const defaultSelection = {
          mode: 'all',
          sheets: workbook.SheetNames
        };
        setSelectedSheet(defaultSelection);
        onSheetSelection(defaultSelection);
      }

    } catch (err) {
      console.error('Error loading sheet info:', err);
      const fileType = file.name.toLowerCase().endsWith('.csv') ? 'CSV' : 'Excel';
      setError(`Failed to load sheet information from ${fileType} file`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectionModeChange = (mode) => {
    let selection;

    if (mode === 'all') {
      selection = {
        mode: 'all',
        sheets: sheets.map(s => s.name)
      };
    } else if (mode === 'single' && sheets.length > 0) {
      selection = {
        mode: 'single',
        sheets: [sheets[0].name]
      };
    }

    setSelectedSheet(selection);
    onSheetSelection(selection);
  };

  const handleSheetSelection = (sheetName) => {
    const selection = {
      mode: 'single',
      sheets: [sheetName]
    };

    setSelectedSheet(selection);
    onSheetSelection(selection);
  };

  /** First three rows of a sheet, to tell one from another at a glance. */
  const renderPreviewTable = (sheetName) => {
    const preview = sheetPreview[sheetName];
    if (!preview || preview.length === 0) return null;
    return (
      <table className="ep-peek">
        <tbody>
          {preview.slice(0, 3).map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.slice(0, 5).map((cell, cellIndex) => <td key={cellIndex}>{cell || ''}</td>)}
              {row.length > 5 ? <td className="ep-peek-more">…</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  if (isLoading) return <EtlLoading>Reading the workbook…</EtlLoading>;
  if (error) return <EtlBanner tone="error">{error}</EtlBanner>;
  if (sheets.length === 0) return null;

  return (
    <>
      <ChoiceCards
        ariaLabel="Sheet selection mode"
        value={selectedSheet?.mode || 'all'}
        onChange={handleSelectionModeChange}
        options={[
          { value: 'all', title: 'Every sheet', sub: `Read all ${sheets.length} sheet${sheets.length === 1 ? '' : 's'} in the workbook.` },
          { value: 'single', title: 'One sheet', sub: 'Read a single named sheet and ignore the rest.' },
        ]}
      />

      {selectedSheet?.mode === 'single' ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 12, maxHeight: '40vh', overflowY: 'auto' }}>
          {sheets.map((sheet) => {
            const chosen = selectedSheet?.sheets?.includes(sheet.name);
            return (
              <button
                key={sheet.index}
                type="button"
                aria-pressed={chosen}
                className={`ep-sheet${chosen ? ' ep-selected' : ''}`}
                onClick={() => handleSheetSelection(sheet.name)}
              >
                <span className="ep-sheet-head">
                  <span className="cp-name">{sheet.name}</span>
                  <span className="cp-code">{sheet.rowCount.toLocaleString()} rows × {sheet.columnCount} cols</span>
                </span>
                {sheet.preview?.length ? renderPreviewTable(sheet.name) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {selectedSheet ? (
        <div className="cp-help" style={{ marginTop: 10 }}>
          {selectedSheet.mode === 'all'
            ? `Reading all ${sheets.length} sheet${sheets.length === 1 ? '' : 's'}.`
            : `Reading “${selectedSheet.sheets.join(', ')}”.`}
        </div>
      ) : null}
    </>
  );
};

export default ExcelSheetSelector;
